import { useEffect, useState } from "react";
import type { ChartResponse, DashboardParams } from "../models/dashboard";
import { CommandGetLaunchChart } from "../services/cmdGetLaunchChart";

/** Answers by chart, filters and sort. The warehouse is frozen, so one stays good for the visit. */
const answers = new Map<string, Promise<ChartResponse>>();

/**
 * One tile's payload, the same useEffect/useState shape as the list and detail pages. Cached
 * so re-sorting a table and coming back does not ask the warehouse twice. Nothing loads until
 * a launch is chosen.
 */
export function useChartData(chartId: string, params: DashboardParams, sort = "") {
    const [data, setData] = useState<ChartResponse | undefined>(undefined);
    const [error, setError] = useState<Error | null>(null);
    const [isLoading, setLoading] = useState(false);
    const key = JSON.stringify([chartId, params, sort]);

    useEffect(() => {
        if (params.launch_id === "") return;
        let live = true;
        setData(undefined);
        setError(null);
        setLoading(true);
        let answer = answers.get(key);
        if (!answer) {
            answer = new CommandGetLaunchChart().run(chartId, params, sort);
            answers.set(key, answer);
            answer.catch(() => answers.delete(key));
        }
        answer
            .then((result) => { if (live) setData(result); })
            .catch((e: unknown) => { if (live) setError(e instanceof Error ? e : new Error(String(e))); })
            .finally(() => { if (live) setLoading(false); });
        return () => { live = false; };
    }, [key]);

    return { data, error, isLoading };
}
